/**
 * Máscara progressiva de telefone brasileiro: formata conforme o usuário digita.
 * Ex: "21" → "(21", "219999" → "(21) 9999", "21999998888" → "(21) 99999-8888".
 *
 * Aceita fixo (10 dígitos), celular (11) e, desde a at-65, número com **código de país**
 * (12 e 13 dígitos, ex: "5521999998888" → "+55 (21) 99999-8888").
 *
 * at-65 (BUG-V55): o teto era 11 dígitos, mas o backend aceita 10 a 13
 * (`normalizePhone`, `src/common/phone.util.ts`) e o próprio formulário valida nesse
 * intervalo — ou seja, havia telefone **válido que não era digitável pela interface**.
 * O telefone é a chave de verificação do link público de checklist (at-08), então um
 * associado com número internacional ficava sem acesso ao checklist dele.
 */

const MAX_DIGITOS = 13;

/** Formata a parte nacional: 10 dígitos (fixo) ou 11 (celular). */
const formatarNacional = (digits: string): string => {
  if (digits.length === 0) return '';
  if (digits.length <= 2) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length <= 10) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
};

export const formatPhoneBr = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, MAX_DIGITOS);

  // Acima de 11 dígitos, os 2 primeiros são código de país — o que sobra é um nacional
  // de 10 (fixo) ou 11 (celular), formatado pela mesma regra.
  if (digits.length > 11) {
    return `+${digits.slice(0, 2)} ${formatarNacional(digits.slice(2))}`;
  }

  return formatarNacional(digits);
};

/** Somente os dígitos do telefone (formato persistido no backend). */
export const phoneDigits = (value: string): string => value.replace(/\D/g, '');
